import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Building2, Plus, Edit, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useDepartments } from '@/hooks/useDepartments';
import { useQueryClient } from '@tanstack/react-query';

export default function DepartmentsSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { departments, isLoading } = useDepartments();
  const [showDialog, setShowDialog] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true,
  });

  const handleOpenNew = () => {
    setEditingDepartment(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      is_active: true,
    });
    setShowDialog(true);
  };

  const handleEdit = (dept) => {
    setEditingDepartment(dept);
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      is_active: dept.is_active,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.code.trim()) {
      toast.error('Nome e Código são obrigatórios');
      return;
    }

    setIsSaving(true);
    try {
      if (editingDepartment) {
        // Update existing
        const { error } = await supabase
          .from('departments')
          .update({
            name: formData.name.trim(),
            code: formData.code.trim().toLowerCase(),
            description: formData.description.trim() || null,
            is_active: formData.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingDepartment.id);

        if (error) throw error;
        toast.success('Departamento atualizado com sucesso');
      } else {
        // Create new
        const { error } = await supabase
          .from('departments')
          .insert({
            name: formData.name.trim(),
            code: formData.code.trim().toLowerCase(),
            description: formData.description.trim() || null,
            is_active: formData.is_active,
          });

        if (error) throw error;
        toast.success('Departamento criado com sucesso');
      }

      queryClient.invalidateQueries(['departments']);
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving department:', error);
      toast.error(error.message || 'Falha ao salvar departamento');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (dept) => {
    try {
      const { error } = await supabase
        .from('departments')
        .update({ is_active: !dept.is_active, updated_at: new Date().toISOString() })
        .eq('id', dept.id);

      if (error) throw error;
      queryClient.invalidateQueries(['departments']);
      toast.success(`Departamento ${!dept.is_active ? 'ativado' : 'desativado'}`);
    } catch (error) {
      console.error('Error toggling department:', error);
      toast.error('Falha ao atualizar departamento');
    }
  };

  return (
    <div className="bg-background p-6 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(createPageUrl('Settings'))}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Departamentos</h1>
              <p className="text-muted-foreground">Gerencie os departamentos da organização</p>
            </div>
          </div>
          <Button onClick={handleOpenNew} className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Departamento
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Todos os Departamentos</CardTitle>
                <CardDescription>Visualize e gerencie todos os departamentos da organização</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando departamentos...</div>
            ) : departments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum departamento encontrado. Crie seu primeiro departamento para começar.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departments.map((dept) => (
                    <TableRow key={dept.id}>
                      <TableCell className="font-medium">{dept.name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{dept.code}</code>
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-md truncate">
                        {dept.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={dept.is_active ? 'default' : 'secondary'}>
                          {dept.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(dept)}
                            className="gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Editar
                          </Button>
                          <Button
                            variant={dept.is_active ? 'secondary' : 'default'}
                            size="sm"
                            onClick={() => handleToggleActive(dept)}
                          >
                            {dept.is_active ? 'Desativar' : 'Ativar'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingDepartment ? 'Editar Departamento' : 'Novo Departamento'}
              </DialogTitle>
              <DialogDescription>
                {editingDepartment
                  ? 'Atualize as informações do departamento abaixo.'
                  : 'Crie um novo departamento para sua organização.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Nome do Departamento <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ex: Vendas, Operações"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">
                  Código <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toLowerCase() })
                  }
                  placeholder="ex: vendas, operacoes"
                />
                <p className="text-xs text-muted-foreground">
                  Identificador único (minúsculas, sem espaços)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Breve descrição do departamento"
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Ativo</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Salvando...' : editingDepartment ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
